/**
 * TeamFormModal Component
 * Modal for adding and editing teams
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Button } from '../ui/Button';
import { teamsService, type TeamFormData } from '../../services/teams.service';
import { membersService } from '../../services/members.service';
import { departmentsService } from '../../services/departments.service';
import { useToast } from '../../hooks/useToast';

export interface TeamFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  teamId?: number;
}

export const TeamFormModal = ({ isOpen, onClose, mode, teamId }: TeamFormModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<TeamFormData>({
    name: '',
    department_id: null,
  });

  const [teamLogo, setTeamLogo] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [selectedManagerIds, setSelectedManagerIds] = useState<number[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);

  // Team name validation state
  const [teamNameError, setTeamNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const nameCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originalNameRef = useRef<string | null>(null);
  const originalDeptIdRef = useRef<number | null>(null);

  // Fetch members for team lead dropdown (exclude removed members)
  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
    select: (data) => data.filter((m) => !m.removed_at),
  });

  // Fetch departments for department dropdown
  const { data: departments = [] } = useQuery({
    queryKey: ['departments-all'],
    queryFn: departmentsService.getAll,
  });

  // Fetch team data for edit mode
  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamsService.getById(teamId!),
    enabled: mode === 'edit' && !!teamId,
  });

  // Populate form when team data loads or when modal opens
  useEffect(() => {
    if (isOpen && mode === 'edit' && team) {
      setFormData({
        name: team.name,
        department_id: team.department_id ?? null,
      });
      setSelectedManagerIds((team.managers ?? []).map((m) => m.member_id));
      setSelectedLeadIds((team.leads ?? []).map((l) => l.member_id));
      // Track original values for edit mode
      originalNameRef.current = team.name;
      originalDeptIdRef.current = team.department_id ?? null;
    }
  }, [isOpen, mode, team]);

  // Load team logo as blob with JWT authentication
  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      if (isOpen && mode === 'edit' && team && team.has_team_logo) {
        try {
          const blobUrl = await teamsService.fetchTeamImage(team.id);
          if (blobUrl && mounted) {
            setCurrentImageUrl(blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load image for team ${team.id}:`, error);
        }
      }
    };

    loadImage();

    // Cleanup: cancel loading if component unmounts
    return () => {
      mounted = false;
    };
  }, [isOpen, mode, team]);

  // Reset form when modal closes or mode changes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        department_id: null,
      });
      setTeamLogo(null);
      setCurrentImageUrl('');
      setSelectedManagerIds([]);
      setSelectedLeadIds([]);
      setTeamNameError(null);
      originalNameRef.current = null;
      originalDeptIdRef.current = null;

      // Clear any pending name check
      if (nameCheckTimeoutRef.current) {
        clearTimeout(nameCheckTimeoutRef.current);
      }

      // Revoke blob URL if team was being edited
      if (mode === 'edit' && teamId) {
        teamsService.revokeTeamImage(teamId);
      }
    }
  }, [isOpen, mode, teamId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: TeamFormData) => teamsService.create(data),
    onSuccess: async () => {
      toast.success('Team created successfully');

      // Clear entire image cache when creating team with logo
      // This ensures the new team's logo loads from server, not stale cache
      if (teamLogo) {
        teamsService.clearTeamImageCache();
      }

      await queryClient.refetchQueries({ queryKey: ['teams'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create team');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: TeamFormData) => teamsService.update(teamId!, data),
    onSuccess: async () => {
      toast.success('Team updated successfully');

      // Always clear the image cache to ensure fresh data on refetch
      if (teamId) {
        teamsService.revokeTeamImage(teamId);
      }

      await queryClient.refetchQueries({ queryKey: ['teams'] });
      await queryClient.refetchQueries({ queryKey: ['team', teamId] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update team');
    },
  });

  // Handle team name change - clear errors as user types
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, name: value }));
    if (teamNameError) {
      setTeamNameError(null);
    }
  };

  // Handle team name blur - check for duplicates
  const handleNameBlur = useCallback(async () => {
    const name = formData.name.trim();

    if (!name) {
      setTeamNameError(null);
      return;
    }

    // Skip if name unchanged in edit mode
    if (mode === 'edit' && name.toLowerCase() === originalNameRef.current?.toLowerCase()) {
      setTeamNameError(null);
      return;
    }

    // Clear pending timeout
    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }

    // Debounce check (500ms)
    setIsCheckingName(true);
    nameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await teamsService.checkNameExists(
          name,
          mode === 'edit' ? teamId : undefined
        );
        if (result.exists) {
          setTeamNameError('Team name already exists');
        } else {
          setTeamNameError(null);
        }
      } catch {
        setTeamNameError(null);
      } finally {
        setIsCheckingName(false);
      }
    }, 500);
  }, [formData.name, mode, teamId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('File must be JPG or PNG');
        e.target.value = '';
        return;
      }

      setTeamLogo(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    // Block submission if duplicate error exists
    if (teamNameError) {
      toast.error(teamNameError);
      return;
    }

    const submitData: TeamFormData = {
      ...formData,
      manager_ids: selectedManagerIds,
      lead_ids: selectedLeadIds,
    };

    if (teamLogo) {
      submitData.team_logo = teamLogo;
    }

    if (mode === 'edit') {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  // When admin picks a member already chosen for the other role, prompt for confirmation
  // instead of silently swapping. Same UX as the detail pages.
  const [pendingSwap, setPendingSwap] = useState<{
    memberId: number;
    memberName: string;
    from: 'manager' | 'lead';
    to: 'manager' | 'lead';
  } | null>(null);

  const handleAddManager = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const id = Number(memberIdStr);
    if (selectedLeadIds.includes(id)) {
      const m = members.find((x) => x.id === id);
      if (m) {
        setPendingSwap({
          memberId: id,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'lead',
          to: 'manager',
        });
      }
      return;
    }
    if (!selectedManagerIds.includes(id)) {
      setSelectedManagerIds((prev) => [...prev, id]);
    }
  };
  const handleRemoveManager = (memberId: number) => {
    setSelectedManagerIds((prev) => prev.filter((id) => id !== memberId));
  };

  const handleAddLead = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const id = Number(memberIdStr);
    if (selectedManagerIds.includes(id)) {
      const m = members.find((x) => x.id === id);
      if (m) {
        setPendingSwap({
          memberId: id,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'manager',
          to: 'lead',
        });
      }
      return;
    }
    if (!selectedLeadIds.includes(id)) {
      setSelectedLeadIds((prev) => [...prev, id]);
    }
  };
  const handleRemoveLead = (memberId: number) => {
    setSelectedLeadIds((prev) => prev.filter((id) => id !== memberId));
  };

  const confirmSwap = () => {
    if (!pendingSwap) return;
    const { memberId, from, to } = pendingSwap;
    setPendingSwap(null);
    if (from === 'manager') {
      setSelectedManagerIds((prev) => prev.filter((id) => id !== memberId));
    } else {
      setSelectedLeadIds((prev) => prev.filter((id) => id !== memberId));
    }
    if (to === 'manager') {
      setSelectedManagerIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    } else {
      setSelectedLeadIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    }
  };

  // Picker candidates exclude only the same-role selections; cross-role members stay
  // selectable so admin can opt into the swap flow via the confirmation modal.
  const activeMembers = members.filter((m) => !m.removed_at);
  const availableManagerMembers = activeMembers.filter((m) => !selectedManagerIds.includes(m.id));
  const availableLeadMembers = activeMembers.filter((m) => !selectedLeadIds.includes(m.id));

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'Edit Team' : 'Add Team'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Team Name */}
        <Input
          label="Team Name"
          name="name"
          value={formData.name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="e.g., Engineering Team"
          required
          error={teamNameError || undefined}
          helperText={isCheckingName ? 'Checking name...' : undefined}
        />

        {/* Team Managers */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Team Managers
          </label>

          {selectedManagerIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedManagerIds.map((managerId) => {
                const member = members.find((m) => m.id === managerId);
                if (!member) return null;
                return (
                  <span
                    key={managerId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-status-warning text-status-warning border border-status-warning"
                  >
                    {member.first_name} {member.last_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveManager(managerId)}
                      className="opacity-70 hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <SearchableSelect
            value=""
            onChange={handleAddManager}
            options={[
              { value: '', label: 'Select a member...' },
              ...availableManagerMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members to add..."
          />
          <p className="text-xs text-meta mt-1">
            Team managers can be any active member.
          </p>
        </div>

        {/* Team Leads */}
        <div>
          <label className="block text-sm font-medium text-body mb-1">
            Team Leads
          </label>

          {selectedLeadIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedLeadIds.map((leadId) => {
                const member = members.find((m) => m.id === leadId);
                if (!member) return null;
                return (
                  <span
                    key={leadId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-status-info text-status-info border border-status-info"
                  >
                    {member.first_name} {member.last_name}
                    <button
                      type="button"
                      onClick={() => handleRemoveLead(leadId)}
                      className="opacity-70 hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <SearchableSelect
            value=""
            onChange={handleAddLead}
            options={[
              { value: '', label: 'Select a member...' },
              ...availableLeadMembers.map((member) => ({
                value: String(member.id),
                label: `${member.first_name} ${member.last_name}`,
              })),
            ]}
            placeholder="Search members to add..."
          />
          <p className="text-xs text-meta mt-1">
            Team leads can be any member, not necessarily from this team.
          </p>
        </div>

        {/* Department */}
        <div>
          <SearchableSelect
            label="Department"
            value={String(formData.department_id || '')}
            onChange={(value) => setFormData((prev) => ({ ...prev, department_id: value ? Number(value) : null }))}
            options={[
              { value: '', label: 'No Department' },
              ...departments.map((dept) => ({
                value: String(dept.id),
                label: dept.name,
              })),
            ]}
            placeholder="Search departments..."
          />
          <p className={`text-xs mt-1 ${
            mode === 'edit' && (formData.department_id ? Number(formData.department_id) : null) !== originalDeptIdRef.current
              ? 'text-amber-600 dark:text-amber-400 font-medium'
              : 'text-meta'
          }`}>
            Assigning a department will update all team members' department.
          </p>
        </div>

        {/* Team Logo */}
        <div>
          <label className="block text-sm font-medium text-body mb-2">Team Logo</label>
          <div className="flex items-center space-x-4">
            {mode === 'edit' && currentImageUrl && (
              <div className="flex-shrink-0">
                <img
                  src={currentImageUrl}
                  alt="Current"
                  className="w-16 h-16 rounded-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                id="team_logo"
                name="team_logo"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-meta file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/20 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30"
              />
              <p className="text-xs text-meta mt-1">
                JPG or PNG, max 5MB. Image will be cropped to 160x160px.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {mode === 'edit' ? 'Update Team' : 'Create Team'}
          </Button>
        </div>
      </form>

      {/* Role-swap Confirmation (overlay sits above the modal) */}
      {pendingSwap && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Change role</h3>
            <p className="text-sm text-meta mb-6">
              <strong>{pendingSwap.memberName}</strong> is currently selected as{' '}
              <strong>team {pendingSwap.from}</strong>. Assigning them as{' '}
              <strong>team {pendingSwap.to}</strong> will remove the team {pendingSwap.from} selection.
            </p>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="secondary" onClick={() => setPendingSwap(null)}>
                Cancel
              </Button>
              <Button type="button" onClick={confirmSwap}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
